import { EnvConfig } from '@gripp/shared-logic';
import {
  ApplicationInsights,
  DistributedTracingModes,
  ITelemetryItem,
} from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { useEffect } from 'react';

const connectionString = EnvConfig.appInsightsConnectionString;

export const reactPlugin = new ReactPlugin();
export let appInsights: ApplicationInsights | null = null;

// Current identity, stamped onto every telemetry item by the single initializer
// below — held in module state rather than re-registering initializers, which
// cannot be removed and would leak one registration per workspace switch / login.
let currentUserId: string | null = null;
let currentWorkspaceId: string | null = null;

/**
 * Attach the authenticated account and current workspace to telemetry. `userId`
 * (the Gripp account id) populates the native authenticated-user dimension
 * (UserAuthenticatedId), which lights up the portal's Users/Sessions/Funnels;
 * both ids also ride along as custom dimensions for ad-hoc KQL filtering. Re-runs
 * when either id changes (e.g. workspace switch) and clears on unmount (logout).
 * Mount once, high in the authenticated tree.
 */
export const useTelemetryContext = (
  userId: string | null | undefined,
  workspaceId: string | null | undefined
): void => {
  // Authenticated user: set on login, cleared on logout (unmount). Kept separate
  // from the workspace effect so a workspace switch never churns the user context.
  useEffect(() => {
    if (!userId) {
      return;
    }
    currentUserId = userId;
    appInsights?.setAuthenticatedUserContext(userId);
    return () => {
      currentUserId = null;
      appInsights?.clearAuthenticatedUserContext();
    };
  }, [userId]);

  // Current workspace: updates on every switch, cleared on unmount.
  useEffect(() => {
    currentWorkspaceId = workspaceId ?? null;
    return () => {
      currentWorkspaceId = null;
    };
  }, [workspaceId]);
};

if (connectionString) {
  let correlationHeaderDomains: string[] = [];
  try {
    const apiHost = new URL(EnvConfig.apiUrl).hostname;
    if (apiHost) {
      correlationHeaderDomains = [apiHost];
    }
  } catch {
    // apiUrl is empty or malformed in this environment — skip domain correlation
  }

  const instance = new ApplicationInsights({
    config: {
      connectionString,
      enableAutoRouteTracking: true,
      distributedTracingMode: DistributedTracingModes.W3C,
      enableCorsCorrelation: true,
      correlationHeaderDomains,
      loggingLevelConsole: 0,
      disableFetchTracking: false,
      extensions: [reactPlugin],
    },
  });

  instance.loadAppInsights();

  instance.addTelemetryInitializer((envelope: ITelemetryItem) => {
    if (!envelope.tags) {
      envelope.tags = {};
    }
    envelope.tags['ai.cloud.role'] = 'gripp-web';
    envelope.tags['ai.application.ver'] = EnvConfig.version;
    if (currentUserId || currentWorkspaceId) {
      envelope.data = envelope.data || {};
      if (currentUserId) {
        envelope.data['userId'] = currentUserId;
      }
      if (currentWorkspaceId) {
        envelope.data['workspaceId'] = currentWorkspaceId;
      }
    }
  });

  appInsights = instance;
}
